// <OrgEmployeeCard> — Org View Step 5 (T-051, spec §6.2).
//
// Single-employee card in the People grid. Click the body → opens
// the Profile page; click the MoreVertical icon → opens the overflow
// menu (handled by <OrgEmployeeCardMenu>) without bubbling to the body.

import { MoreVertical } from 'lucide-react';
import type { OrgViewEmployee } from '@pa/shared';
import { OrgEmployeeCardMenu, type OrgViewerRole } from './OrgEmployeeCardMenu';
import { DENSITY_CLASSES, type OrgDensity } from './OrgDensityToggle';

export interface OrgEmployeeCardProps {
  employee: OrgViewEmployee;
  viewerRole: OrgViewerRole;
  /** Called when the user clicks the card body (not the menu icon). */
  onOpenProfile: (id: string) => void;
  /** Called when the menu's Quick edit row is clicked. HR-only. */
  onQuickEdit: (id: string) => void;
  /** Called when Send message is clicked (default: mailto:). */
  onSendMessage: (employee: OrgViewEmployee) => void;
  /** Called when Deactivate is confirmed. HR-only. */
  onDeactivate: (employee: OrgViewEmployee) => void;
  /** Spec §6.1.3 — controls per-card padding + avatar size. */
  density?: OrgDensity;
}

const STATUS_DOT_BG: Record<OrgViewEmployee['status'], string> = {
  active: 'bg-emerald-500',
  inactive: 'bg-slate-300',
};

const STATUS_LABEL: Record<OrgViewEmployee['status'], string> = {
  active: 'Active',
  inactive: 'Inactive',
};

export function OrgEmployeeCard({
  employee,
  viewerRole,
  onOpenProfile,
  onQuickEdit,
  onSendMessage,
  onDeactivate,
  density = 'comfortable',
}: OrgEmployeeCardProps) {
  const densityClasses = DENSITY_CLASSES[density];
  function onCardKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpenProfile(employee.id);
    }
  }
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpenProfile(employee.id)}
      onKeyDown={onCardKeyDown}
      data-testid={`org-employee-card-${employee.id}`}
      data-density={density}
      className={`bg-white rounded-3xl border border-slate-200 shadow-sm ${densityClasses.cardPadding} flex flex-col items-center text-center cursor-pointer hover:border-[#6344C6]/30 hover:shadow-md transition-all group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6344C6]/40`}
    >
      <CardAvatar
        name={employee.name}
        avatarColor={employee.avatarColor}
        avatarUrl={employee.avatarUrl}
        sizeClass={densityClasses.avatarSize}
        textSizeClass={densityClasses.avatarText}
      />
      <h4 className="font-bold text-slate-800 group-hover:text-[#6344C6] text-sm leading-tight">
        {employee.name}
      </h4>
      {employee.title && (
        <p className="text-xs text-slate-400 mt-1">{employee.title}</p>
      )}
      {employee.teamName && (
        <p className="text-xs font-bold text-slate-500 mt-3 bg-slate-50 px-3 py-1 rounded-full">
          {employee.teamName}
        </p>
      )}
      <div className="mt-4 flex items-center gap-1.5">
        <span
          aria-hidden
          className={`w-2 h-2 rounded-full ${STATUS_DOT_BG[employee.status]}`}
        />
        <span
          className="text-[10px] font-bold text-slate-400 tracking-wider uppercase"
          data-testid={`org-employee-status-${employee.id}`}
        >
          {STATUS_LABEL[employee.status]}
        </span>
      </div>
      {/* Overflow menu lives at the bottom of the card. The trigger is
       *  hidden by default and revealed on hover/focus per spec §6.2. */}
      <OrgEmployeeCardMenu
        employee={employee}
        viewerRole={viewerRole}
        onOpenProfile={onOpenProfile}
        onQuickEdit={onQuickEdit}
        onSendMessage={onSendMessage}
        onDeactivate={onDeactivate}
      >
        {(triggerProps) => (
          <button
            type="button"
            {...triggerProps}
            data-testid={`org-employee-menu-trigger-${employee.id}`}
            aria-label={`Open menu for ${employee.name}`}
            className="mt-4 p-1 text-slate-300 hover:text-slate-500 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6344C6]/40 rounded"
          >
            <MoreVertical size={18} aria-hidden />
          </button>
        )}
      </OrgEmployeeCardMenu>
    </div>
  );
}

function CardAvatar({
  name,
  avatarColor,
  avatarUrl,
  sizeClass,
  textSizeClass,
}: {
  name: string;
  avatarColor: string;
  avatarUrl: string | null;
  sizeClass: string;
  textSizeClass: string;
}) {
  const initials = computeInitials(name);
  if (avatarUrl) {
    return (
      <span
        className={`${sizeClass} rounded-full overflow-hidden mb-4 shadow-inner`}
      >
        <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
      </span>
    );
  }
  return (
    <span
      className={`${sizeClass} ${textSizeClass} rounded-full mb-4 shadow-inner flex items-center justify-center text-white font-bold ${avatarColor}`}
      data-testid="org-employee-avatar-fallback"
    >
      {initials}
    </span>
  );
}

// First letter of the first up-to-2 name parts, uppercased.
// "Ada Lovelace" → "AL"; "Madonna" → "M"; "" → "?"
export function computeInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
}
